import type {Reducer, UnknownAction} from '@reduxjs/toolkit'

// ── Types ─────────────────────────────────────────────────────────

interface SceneHistory<S> {
  past: S[]
  future: S[]
}

export interface UndoableState<T> {
  past: T[]
  present: T
  future: T[]
  /** Per-scene undo/redo stacks — stashed when switching away from a scene */
  historyByScene: Record<number, SceneHistory<T>>
  /** Last action type — used to detect merge chains for mergeable actions */
  lastActionType: string | null
}

/**
 * Slice-aware config: undo/redo only snapshots (and restores) the sub-state
 * returned by `getUndoSlice`, leaving the rest of the state untouched.
 */
interface UndoableConfig<T> {
  /** Action types that update present without creating an undo entry */
  transparentActionTypes?: Set<string>
  /** Action types that merge consecutive dispatches into one undo entry */
  mergeableActionTypes?: Set<string>
  /** Extract the sub-state that undo/redo tracks (e.g. active scene). If omitted, tracks whole T. */
  getUndoSlice?: (state: T) => unknown
  /** Write back the sub-state after undo/redo. Required when getUndoSlice is provided. */
  setUndoSlice?: (state: T, slice: unknown) => T
}

// ── Actions ───────────────────────────────────────────────────────

export const UNDO = '@@undoable/UNDO'
export const REDO = '@@undoable/REDO'
export const SEAL_UNDO = '@@undoable/SEAL_UNDO'
export const SWITCH_SCENE_HISTORY = '@@undoable/SWITCH_SCENE_HISTORY'
export const REMOVE_SCENE_HISTORY = '@@undoable/REMOVE_SCENE_HISTORY'

export const undo = () => ({type: UNDO}) as const
export const redo = () => ({type: REDO}) as const

/** Reset merge chain — call on mouseUp / interaction end */
export const sealUndo = () => ({type: SEAL_UNDO}) as const

/** Stash history for `fromScene`, restore history for `toScene` */
export const switchSceneHistory = (payload: {fromScene: number; toScene: number}) =>
  ({type: SWITCH_SCENE_HISTORY, payload}) as const

/** Remove stashed history for a deleted scene and re-index higher scenes */
export const removeSceneHistory = (payload: {removedIndex: number}) => ({type: REMOVE_SCENE_HISTORY, payload}) as const

// ── History limit ─────────────────────────────────────────────────

const MAX_HISTORY = 50

// ── Reducer enhancer ──────────────────────────────────────────────

/**
 * Wraps a reducer with undo/redo history tracking.
 *
 * When `getUndoSlice` / `setUndoSlice` are provided, only the extracted slice
 * is stored in past/future — undo restores just that slice while leaving the
 * rest of the state (e.g. scenes array length, activeSceneIndex) intact.
 */
const undoable = <T>(reducer: Reducer<T>, config: UndoableConfig<T> = {}): Reducer<UndoableState<T>> => {
  const {transparentActionTypes = new Set(), mergeableActionTypes = new Set(), getUndoSlice, setUndoSlice} = config
  const sliceAware = !!(getUndoSlice && setUndoSlice)
  const initialPresent = reducer(undefined, {type: '@@INIT'} as UnknownAction)
  const initialState: UndoableState<T> = {
    past: [],
    present: initialPresent,
    future: [],
    historyByScene: {},
    lastActionType: null,
  }

  return (state: UndoableState<T> | undefined = initialState, action: UnknownAction): UndoableState<T> => {
    const {past, present, future, historyByScene, lastActionType} = state

    switch (action.type) {
      case UNDO: {
        if (past.length === 0) return state

        if (sliceAware) {
          // Restore only the tracked slice from the past snapshot
          const previousSlice = past[past.length - 1]
          const currentSlice = getUndoSlice!(present) as T
          const newPast = past.slice(0, past.length - 1)
          // Apply the old slice onto the current full present
          const restoredPresent = setUndoSlice!(present, previousSlice)
          return {
            past: newPast,
            present: restoredPresent,
            future: [currentSlice, ...future],
            historyByScene,
            lastActionType: null,
          }
        }

        const previous = past[past.length - 1]
        const newPast = past.slice(0, past.length - 1)
        return {past: newPast, present: previous, future: [present, ...future], historyByScene, lastActionType: null}
      }

      case REDO: {
        if (future.length === 0) return state

        if (sliceAware) {
          const nextSlice = future[0]
          const currentSlice = getUndoSlice!(present) as T
          const newFuture = future.slice(1)
          const restoredPresent = setUndoSlice!(present, nextSlice)
          return {
            past: [...past, currentSlice],
            present: restoredPresent,
            future: newFuture,
            historyByScene,
            lastActionType: null,
          }
        }

        const next = future[0]
        const newFuture = future.slice(1)
        return {past: [...past, present], present: next, future: newFuture, historyByScene, lastActionType: null}
      }

      case SWITCH_SCENE_HISTORY: {
        const {fromScene, toScene} = (action as ReturnType<typeof switchSceneHistory>).payload

        // Stash current past/future for the scene we're leaving
        const newHistoryByScene = {
          ...historyByScene,
          [fromScene]: {past, future},
        }

        // Restore the target scene's history (empty if never visited)
        const restored = newHistoryByScene[toScene] ?? {past: [], future: []}

        return {
          past: restored.past,
          present,
          future: restored.future,
          historyByScene: newHistoryByScene,
          lastActionType: null,
        }
      }

      case REMOVE_SCENE_HISTORY: {
        const {removedIndex} = (action as ReturnType<typeof removeSceneHistory>).payload
        const newMap: Record<number, SceneHistory<T>> = {}

        for (const [keyStr, value] of Object.entries(historyByScene)) {
          const key = Number(keyStr)
          if (key === removedIndex) continue
          const newKey = key > removedIndex ? key - 1 : key
          newMap[newKey] = value
        }

        return {past: [], present, future: [], historyByScene: newMap, lastActionType: null}
      }

      case SEAL_UNDO: {
        return {...state, lastActionType: null}
      }

      default: {
        const newPresent = reducer(present, action)
        if (present === newPresent) return state

        // Transparent actions: update present without creating an undo entry
        if (transparentActionTypes.has(action.type)) {
          return {past, present: newPresent, future, historyByScene, lastActionType}
        }

        // Mergeable actions: consecutive same-type dispatches share one undo entry
        const isMergeable = mergeableActionTypes.has(action.type)
        const shouldMerge = isMergeable && lastActionType === action.type

        if (sliceAware) {
          const oldSlice = getUndoSlice!(present)
          const newSlice = getUndoSlice!(newPresent)
          // If the tracked slice didn't change, treat as transparent
          if (oldSlice === newSlice) {
            return {past, present: newPresent, future, historyByScene, lastActionType}
          }
          if (shouldMerge) {
            return {past, present: newPresent, future: [], historyByScene, lastActionType: action.type}
          }
          const newPast =
            past.length >= MAX_HISTORY ? ([...past.slice(1), oldSlice] as T[]) : ([...past, oldSlice] as T[])
          return {past: newPast, present: newPresent, future: [], historyByScene, lastActionType: action.type}
        }

        if (shouldMerge) {
          return {past, present: newPresent, future: [], historyByScene, lastActionType: action.type}
        }

        const newPast = past.length >= MAX_HISTORY ? [...past.slice(1), present] : [...past, present]
        return {past: newPast, present: newPresent, future: [], historyByScene, lastActionType: action.type}
      }
    }
  }
}

export default undoable
